import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../i18n';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ReviewIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  getExceptionQueue,
  approveDailyFee,
  rejectDailyFee,
  getDocument,
} from '../services/api';
import { ExceptionCase, ComparisonResult } from '../types';

const ExceptionQueue: React.FC = () => {
  const { t } = useLanguage();
  const [exceptions, setExceptions] = useState<ExceptionCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedException, setSelectedException] = useState<ExceptionCase | null>(null);
  const [selectedComparisonResult, setSelectedComparisonResult] = useState<ComparisonResult[] | null>(null);
  const [selectedExtractedData, setSelectedExtractedData] = useState<Record<string, any> | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadExceptions();
  }, []);

  const loadExceptions = async () => {
    try {
      setLoading(true);
      const data = await getExceptionQueue();
      setExceptions(data);
    } catch (error) {
      console.error('Failed to load exceptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (exception: ExceptionCase) => {
    setSelectedException(exception);
    // Use comparison_result from the exception itself first
    setSelectedComparisonResult(exception.comparison_result || null);
    setSelectedExtractedData(null);
    setReviewDialogOpen(true);
    // Fetch full document to get extracted_data (actual field values) + stored comparison_result
    setLoadingDetails(true);
    try {
      const fullDoc = await getDocument(exception.document_id);
      // Store extracted_data — contains the actual dynamic fields from the document
      const allFields: Record<string, any> =
        (fullDoc.extracted_data as any)?.all_fields ||
        (fullDoc.extracted_data as any)?.field_details ||
        {};
      setSelectedExtractedData(Object.keys(allFields).length > 0 ? allFields : null);
      // Prefer stored comparison_result if it has actual values
      if (fullDoc.comparison_result && fullDoc.comparison_result.length > 0) {
        setSelectedComparisonResult(fullDoc.comparison_result);
      }
    } catch (err) {
      console.warn('Could not load full document details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  /**
   * Build merged rows for the comparison table.
   * Priority:
   * 1. Use comparison_result as primary source — it has BOTH document and ERP values with match status.
   * 2. Append any remaining extracted fields not already covered by comparison_result.
   * 3. Fall back to extracted_data alone if no comparison_result available.
   */
  const buildMergedRows = (): Array<{ field: string; docValue: string; erpValue: string; match: boolean | null; hasData: boolean }> => {
    const rows: Array<{ field: string; docValue: string; erpValue: string; match: boolean | null; hasData: boolean }> = [];
    const coveredFields = new Set<string>();

    // Step 1: Add all comparison_result rows first — they have real doc vs ERP data
    if (selectedComparisonResult && selectedComparisonResult.length > 0) {
      selectedComparisonResult.forEach(r => {
        rows.push({
          field: r.field,
          docValue: r.document_value || '',
          erpValue: r.erp_value || '',
          hasData: !!(r.document_value || r.erp_value),
          match: r.match,
        });
        // Track this field name (normalized) so we don't duplicate
        coveredFields.add(r.field.toLowerCase().replace(/[\s_-]+/g, '_'));
      });
    }

    // Step 2: Append remaining extracted fields not already covered by comparison
    if (selectedExtractedData && Object.keys(selectedExtractedData).length > 0) {
      Object.entries(selectedExtractedData).forEach(([key, fieldValue]) => {
        const normalizedKey = key.toLowerCase().replace(/[\s_-]+/g, '_');
        // Skip if already covered by comparison results
        if (coveredFields.has(normalizedKey)) return;

        const fv = fieldValue as any;
        const docValue =
          (typeof fv === 'string' ? fv : null) ||
          fv?.normalized ||
          fv?.translated ||
          fv?.english_text ||
          fv?.original_text ||
          fv?.original ||
          '';
        if (!docValue) return; // Skip empty extracted fields

        rows.push({
          field: key,
          docValue,
          erpValue: '',
          hasData: !!docValue,
          match: null, // No ERP comparison for this field
        });
      });
    }

    return rows;
  };

  const handleApprove = useCallback(async () => {
    if (!selectedException) return;
    setActionLoading('approve');
    try {
      await approveDailyFee(selectedException.document_id);
      setExceptions(prev => prev.filter(e => e.document_id !== selectedException.document_id));
      setReviewDialogOpen(false);
      setSelectedException(null);
      setSelectedComparisonResult(null);
      setSelectedExtractedData(null);
    } catch (error) {
      console.error('Failed to approve:', error);
      // Reload on error to ensure consistency
      loadExceptions();
    } finally {
      setActionLoading(null);
    }
  }, [selectedException]);

  const handleReject = useCallback(async () => {
    if (!selectedException || !rejectReason) return;
    setActionLoading('reject');
    try {
      await rejectDailyFee(selectedException.document_id, rejectReason);
      setExceptions(prev => prev.filter(e => e.document_id !== selectedException.document_id));
      setRejectDialogOpen(false);
      setReviewDialogOpen(false);
      setRejectReason('');
      setSelectedException(null);
      setSelectedComparisonResult(null);
      setSelectedExtractedData(null);
    } catch (error) {
      console.error('Failed to reject:', error);
      // Reload on error to ensure consistency
      loadExceptions();
    } finally {
      setActionLoading(null);
    }
  }, [selectedException, rejectReason]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
          }}
        >
          <WarningIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {t.exceptionQueuePage.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t.exceptionQueuePage.subtitle}
          </Typography>
        </Box>
        {exceptions.length > 0 && (
          <Chip 
            label={`${exceptions.length} ${t.exceptionQueuePage.pending}`} 
            color="warning" 
            sx={{ ml: 'auto', fontWeight: 600 }}
          />
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : exceptions.length === 0 ? (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {t.exceptionQueuePage.noExceptions}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha('#F59E0B', 0.1) }}>
                <TableCell sx={{ fontWeight: 600 }}>{t.exceptionQueuePage.documentId}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t.exceptionQueuePage.deliveryNumber}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t.exceptionQueuePage.filename}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t.exceptionQueuePage.exceptionReasons}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t.exceptionQueuePage.uploadedAt}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t.exceptionQueuePage.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exceptions.map((exception) => (
                <TableRow 
                  key={exception.document_id}
                  sx={{ 
                    '&:hover': { backgroundColor: alpha('#F59E0B', 0.05) },
                    transition: 'background-color 0.2s',
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {exception.document_id.substring(0, 8)}...
                    </Typography>
                  </TableCell>
                  <TableCell>{exception.delivery_number || 'N/A'}</TableCell>
                  <TableCell>{exception.filename}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {exception.exception_reasons.slice(0, 2).map((reason, idx) => (
                        <Chip
                          key={idx}
                          label={reason}
                          size="small"
                          color="warning"
                        />
                      ))}
                      {exception.exception_reasons.length > 2 && (
                        <Chip
                          label={`+${exception.exception_reasons.length - 2} ${t.exceptionQueuePage.more}`}
                          size="small"
                          variant="outlined"
                          color="warning"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(exception.uploaded_at).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<ReviewIcon />}
                      onClick={() => handleReview(exception)}
                      sx={{
                        background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                        },
                        flexDirection: 'column',
                        lineHeight: 1.1,
                        py: 0.75,
                        px: 1.5,
                        minWidth: 120,
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 700 }}>{t.exceptionQueuePage.review}</Box>
                      <Box component="span" sx={{ fontSize: '0.6rem', fontWeight: 500, opacity: 0.85, letterSpacing: '0.03em' }}>HITL (Human in the Loop)</Box>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1 }}>{t.exceptionQueuePage.reviewExceptionCase}</Box>
            <Chip
              label="HITL — Human in the Loop"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.65rem',
                letterSpacing: '0.04em',
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedException && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t.exceptionQueuePage.exceptionReasons}
              </Typography>
              <ul>
                {selectedException.exception_reasons.map((reason, idx) => (
                  <li key={idx}>{reason}</li>
                ))}
              </ul>

              {/* Comparison Results — built from actual extracted fields + ERP comparison */}
              {loadingDetails ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">Loading Field · Document · ERP · Match data...</Typography>
                </Box>
              ) : (() => {
                const rows = buildMergedRows();
                if (rows.length === 0) {
                  return (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No comparison data available. Run the document through ERP Comparison to generate field matching results.
                    </Alert>
                  );
                }
                const hasAnyData = rows.some(r => r.hasData);
                const matchCount = rows.filter(r => r.match === true).length;
                const mismatchCount = rows.filter(r => r.match === false).length;
                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
                        {t.exceptionQueuePage.comparisonResults}
                      </Typography>
                      {hasAnyData && (
                        <>
                          <Chip label={`✓ ${matchCount} Match`} size="small" sx={{ backgroundColor: alpha('#10B981', 0.15), color: '#10B981', fontWeight: 700, fontSize: '0.7rem' }} />
                          <Chip label={`✗ ${mismatchCount} Mismatch`} size="small" sx={{ backgroundColor: alpha('#EF4444', 0.15), color: '#EF4444', fontWeight: 700, fontSize: '0.7rem' }} />
                        </>
                      )}
                    </Box>
                    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha('#8B5CF6', 0.1) }}>
                            <TableCell sx={{ fontWeight: 700, width: '28%' }}>{t.exceptionQueuePage.field}</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: '28%' }}>📄 {t.exceptionQueuePage.document}</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: '28%' }}>🏢 {t.exceptionQueuePage.erp}</TableCell>
                            <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: '16%' }}>{t.exceptionQueuePage.match}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((row) => (
                            <TableRow
                              key={row.field}
                              sx={{
                                backgroundColor: !row.hasData
                                  ? alpha('#6B7280', 0.03)
                                  : row.match === true
                                    ? alpha('#10B981', 0.04)
                                    : row.match === false
                                      ? alpha('#EF4444', 0.05)
                                      : 'transparent',
                                '&:hover': {
                                  backgroundColor: row.match === true
                                    ? alpha('#10B981', 0.09)
                                    : row.match === false
                                      ? alpha('#EF4444', 0.1)
                                      : alpha('#6B7280', 0.08),
                                },
                              }}
                            >
                              <TableCell>
                                <strong>{row.field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</strong>
                              </TableCell>
                              <TableCell sx={{
                                fontFamily: row.docValue ? 'monospace' : 'inherit',
                                color: row.docValue ? 'text.primary' : 'text.disabled',
                                fontSize: '0.82rem',
                              }}>
                                {row.docValue || '— Not extracted'}
                              </TableCell>
                              <TableCell sx={{
                                fontFamily: row.erpValue ? 'monospace' : 'inherit',
                                color: row.erpValue ? 'text.primary' : 'text.disabled',
                                fontSize: '0.82rem',
                              }}>
                                {row.erpValue || '— Not in ERP'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                {row.match === null || !row.hasData ? (
                                  <Chip label="— N/A" size="small" sx={{ backgroundColor: alpha('#6B7280', 0.12), color: '#9CA3AF', fontWeight: 600, fontSize: '0.68rem' }} />
                                ) : row.match ? (
                                  <Chip label="✓ Match" size="small" sx={{ backgroundColor: alpha('#10B981', 0.15), color: '#10B981', fontWeight: 700, fontSize: '0.7rem' }} />
                                ) : (
                                  <Chip label="✗ Mismatch" size="small" sx={{ backgroundColor: alpha('#EF4444', 0.15), color: '#EF4444', fontWeight: 700, fontSize: '0.7rem' }} />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setReviewDialogOpen(false)}
            disabled={actionLoading !== null}
          >
            {t.common.cancel}
          </Button>
          <Button
            onClick={() => setRejectDialogOpen(true)}
            color="error"
            variant="outlined"
            startIcon={actionLoading === 'reject' ? <CircularProgress size={16} /> : <RejectIcon />}
            disabled={actionLoading !== null}
            sx={{ minWidth: 100 }}
          >
            {t.common.reject}
          </Button>
          <Button 
            onClick={handleApprove} 
            variant="contained"
            startIcon={actionLoading === 'approve' ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon />}
            disabled={actionLoading !== null}
            sx={{
              minWidth: 100,
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              },
            }}
          >
            {actionLoading === 'approve' ? t.exceptionQueuePage.approving : t.common.approve}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>{t.exceptionQueuePage.rejectDailyFee}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t.exceptionQueuePage.rejectionReason}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            rows={4}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setRejectDialogOpen(false)}
            disabled={actionLoading !== null}
          >
            {t.common.cancel}
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            startIcon={actionLoading === 'reject' ? <CircularProgress size={16} color="inherit" /> : <RejectIcon />}
            disabled={!rejectReason || actionLoading !== null}
          >
            {actionLoading === 'reject' ? t.exceptionQueuePage.rejecting : t.exceptionQueuePage.confirmReject}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExceptionQueue;

